import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import {
  Text,
  Group,
  Badge,
  Button,
  Anchor,
  Stack,
  Grid,
  Paper,
  ActionIcon,
  Modal,
  Tooltip,
  Avatar,
  ThemeIcon,
  Box,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { useDeleteAlternativeContact } from "../hooks/useContacts"
import { useUser } from "../../user/hooks/useUser"
import ContactAlternativeForm from "./ContactAlternativeForm"
import type { Contact, ContactAlternative } from "../models/contact"

function EmptyValue() {
  return (
    <Text size="sm" fs="italic" c="gray.5">
      <Trans i18nKey="common.notFilled" />
    </Text>
  )
}

function initials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()
}

interface Props {
  contact: Contact
}

export default function ContactDetailContent({ contact }: Props) {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageContacts ?? false
  const deleteAlt = useDeleteAlternativeContact()

  const [editingAlt, setEditingAlt] = useState<ContactAlternative | null>(null)
  const [altModalOpened, { open: openAltModal, close: closeAltModal }] = useDisclosure(false)

  const handleDeleteAlt = (alt: ContactAlternative) => {
    modals.openConfirmModal({
      title: t("contacts.confirmDeleteAlt"),
      children: <Text size="sm">{t("contacts.confirmDeleteAltMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => deleteAlt.mutate(alt.id),
    })
  }

  const handleEditAlt = (alt: ContactAlternative) => {
    setEditingAlt(alt)
    openAltModal()
  }

  const handleAddAlt = () => {
    setEditingAlt(null)
    openAltModal()
  }

  const handleAltFormSuccess = () => {
    closeAltModal()
    setEditingAlt(null)
  }

  const hasAltContacts = contact.alternative_contacts && contact.alternative_contacts.length > 0

  return (
    <Stack>
      <Paper withBorder p="md">
        <Group>
          <Avatar size={60} radius={60} color="blue">
            {initials(contact.name)}
          </Avatar>
          <Box style={{ flex: 1 }}>
            <Group gap="xs" mb={4}>
              <Text fw={700} size="xl">{contact.name}</Text>
              <Badge variant="light" size="sm">{contact.type_name}</Badge>
            </Group>
            <Group gap="lg">
              {contact.email && (
                <Text size="sm" c="dimmed">
                  <i className="fa fa-envelope" style={{ width: 16 }} /> {contact.email}
                </Text>
              )}
              {contact.phone_number && (
                <Text size="sm" c="dimmed">
                  <i className="fa fa-phone" style={{ width: 16 }} /> {contact.phone_number}
                </Text>
              )}
            </Group>
          </Box>
        </Group>
      </Paper>

      <Grid>
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Stack gap="md">
            <Paper withBorder p="md">
              <Group gap="xs" mb="md">
                <ThemeIcon variant="light" color="green" size="sm" radius="xl">
                  <i className="fa fa-address-book-o" style={{ fontSize: 12 }} />
                </ThemeIcon>
                <Text fw={600} size="sm" tt="uppercase" lts="0.05em" c="dimmed">
                  <Trans i18nKey="contacts.contactInfo" />
                </Text>
              </Group>
              <Stack gap="xs">
                <div>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
                    <Trans i18nKey="contacts.email" />
                  </Text>
                  <Text>{contact.email || <EmptyValue />}</Text>
                </div>
                <div>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
                    <Trans i18nKey="contacts.phone" />
                  </Text>
                  <Text>{contact.phone_number || <EmptyValue />}</Text>
                </div>
                <div>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
                    <Trans i18nKey="contacts.mobile" />
                  </Text>
                  <Text>{contact.mobile_number || <EmptyValue />}</Text>
                </div>
                <div>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
                    <Trans i18nKey="contacts.contactPerson" />
                  </Text>
                  <Text>{contact.contact_person || <EmptyValue />}</Text>
                </div>
                <div>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
                    <Trans i18nKey="contacts.emailAlternative" />
                  </Text>
                  <Text>{contact.email_alternative || <EmptyValue />}</Text>
                </div>
              </Stack>
            </Paper>

            <Paper withBorder p="md">
              <Group gap="xs" mb="md">
                <ThemeIcon variant="light" color="violet" size="sm" radius="xl">
                  <i className="fa fa-map-marker" style={{ fontSize: 12 }} />
                </ThemeIcon>
                <Text fw={600} size="sm" tt="uppercase" lts="0.05em" c="dimmed">
                  <Trans i18nKey="contacts.addressInfo" />
                </Text>
              </Group>
              <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
                <Trans i18nKey="contacts.address" />
              </Text>
              <Text mb="sm">{contact.address || <EmptyValue />}</Text>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
                    <Trans i18nKey="contacts.postalCode" />
                  </Text>
                  <Text>{contact.postal_code || <EmptyValue />}</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
                    <Trans i18nKey="contacts.locale" />
                  </Text>
                  <Text>{contact.locale || <EmptyValue />}</Text>
                </Grid.Col>
              </Grid>
            </Paper>

            <Paper withBorder p="md">
              <Group gap="xs" mb="md">
                <ThemeIcon variant="light" color="orange" size="sm" radius="xl">
                  <i className="fa fa-building-o" style={{ fontSize: 12 }} />
                </ThemeIcon>
                <Text fw={600} size="sm" tt="uppercase" lts="0.05em" c="dimmed">
                  <Trans i18nKey="contacts.companyInfo" />
                </Text>
              </Group>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
                    <Trans i18nKey="contacts.nif" />
                  </Text>
                  <Text>{contact.nif || <EmptyValue />}</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
                    <Trans i18nKey="contacts.website" />
                  </Text>
                  {contact.website ? (
                    <Anchor href={contact.website} target="_blank" size="sm">
                      {contact.website}
                    </Anchor>
                  ) : (
                    <EmptyValue />
                  )}
                </Grid.Col>
              </Grid>
            </Paper>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Paper withBorder p="md">
            <Group gap="xs" mb="md">
              <ThemeIcon variant="light" color="cyan" size="sm" radius="xl">
                <i className="fa fa-users" style={{ fontSize: 12 }} />
              </ThemeIcon>
              <Text fw={600} size="sm" tt="uppercase" lts="0.05em" c="dimmed" style={{ flex: 1 }}>
                <Trans i18nKey="contacts.alternativeContacts" />
              </Text>
              {canManage && (
                <Button onClick={handleAddAlt} size="compact-sm" leftSection={<i className="fa fa-plus" />}>
                  <Trans i18nKey="contacts.addAlternative" />
                </Button>
              )}
            </Group>

            {hasAltContacts ? (
              <Stack gap="sm">
                {contact.alternative_contacts.map((alt) => (
                  <Paper key={alt.id} withBorder={false} bg="gray.0" p="sm" radius="md">
                    <Group justify="space-between" align="flex-start">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="xs" mb={4}>
                          <Badge variant="light" size="sm">{alt.type_name}</Badge>
                          {alt.role && <Text size="xs" c="dimmed">{alt.role}</Text>}
                        </Group>
                        {alt.name && <Text size="sm" fw={500}>{alt.name}</Text>}
                        {alt.phone_number && (
                          <Text size="xs" c="dimmed" mt={2}>
                            <i className="fa fa-phone" style={{ width: 14 }} /> {alt.phone_number}
                          </Text>
                        )}
                        {alt.email && (
                          <Text size="xs" c="dimmed">
                            <i className="fa fa-envelope" style={{ width: 14 }} /> {alt.email}
                          </Text>
                        )}
                      </div>
                      {canManage && (
                        <Group gap={4} ml="sm" wrap="nowrap">
                          <Tooltip label={t("common.edit")}>
                            <ActionIcon variant="subtle" size="sm" onClick={() => handleEditAlt(alt)}>
                              <i className="fa fa-pencil" />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t("common.delete")}>
                            <ActionIcon variant="subtle" size="sm" color="red" onClick={() => handleDeleteAlt(alt)}>
                              <i className="fa fa-trash" />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      )}
                    </Group>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Text size="sm" fs="italic" c="gray.5">
                <Trans i18nKey="contacts.noAlternativeContacts" />
              </Text>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      <Modal
        opened={altModalOpened}
        onClose={closeAltModal}
        title={editingAlt ? t("contacts.editAlternative") : t("contacts.addAlternative")}
        size="md"
      >
        <ContactAlternativeForm
          contactId={contact.id}
          editItem={editingAlt}
          onSuccess={handleAltFormSuccess}
          onCancel={closeAltModal}
        />
      </Modal>
    </Stack>
  )
}
